import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Heart,
  ShoppingCart,
  Eye,
  Trash2,
  LayoutGrid,
  List,
  Grid2x2,
  Grid3x3,
  Grid,
} from "lucide-react";
import { useWishlist } from "../../hooks/useWishlist";
import { useCart } from "../../hooks/useCart";
import { useAuth } from "../../context/AuthContext";
import ProductCard from "./ProductCard";
import LightLoader from "../common/LightLoader";
import { SkeletonProductCard } from "../common/SkeletonLoader";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config/api";

const WishlistPage = () => {
  const { wishlist, loading, removeFromWishlist, toggleWishlist } =
    useWishlist();
  const { addToCart } = useCart();
  const { user } = useAuth();

  // Responsive grid column state
  const [gridColumns, setGridColumns] = React.useState(() => {
    if (typeof window !== "undefined" && window.innerWidth >= 1024) {
      return 4;
    }
    return 2;
  });
  const gridOptions = [
    { cols: 1, icon: List },
    { cols: 2, icon: Grid2x2 },
    { cols: 3, icon: Grid3x3 },
    { cols: 4, icon: Grid },
    { cols: 5, icon: LayoutGrid },
  ];

  const handleRemoveFromWishlist = async (productId) => {
    await removeFromWishlist(productId);
  };

  const handleAddToCart = async (product) => {
    // Find the default variant or first available variant
    const defaultVariant =
      product.product_variants?.find((v) => v.is_default) ||
      product.product_variants?.[0];

    if (defaultVariant) {
      await addToCart(defaultVariant.id, 1);
    }
  };

  // Construct proper image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) {
      console.log("No image URL provided, using placeholder");
      return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
    }

    // If it's already a full URL, return as is
    if (imageUrl.startsWith("http")) return imageUrl;
    if (imageUrl.startsWith("/api/placeholder")) return imageUrl;

    const base = (API_BASE_URL || "").replace(/\/+$/, "");
    let cleanUrl = imageUrl
      .replace(/^\/uploads\//, "")
      .replace(/^uploads\//, "");
    const finalUrl = `${base}/uploads/${cleanUrl}`;
    console.log("Constructed image URL:", finalUrl);
    return finalUrl;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <div className="container mx-auto px-4 py-8">
          <LightLoader size="lg" className="mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Helmet>
        <title>My Wishlist - Knots & Krafts</title>
        <meta
          name="description"
          content="View and manage your favorite yarn and craft products in your wishlist."
        />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              My Wishlist
            </h1>
            <p className="text-gray-600">
              {wishlist.length} {wishlist.length === 1 ? "item" : "items"} in
              your wishlist
            </p>
          </div>
          {/* Grid Switcher */}
          <div className="flex items-center space-x-2">
            {gridOptions.map((option) => {
              // Only show up to 2 columns on mobile
              if (window.innerWidth < 640 && option.cols > 2) return null;
              // Only show up to 5 columns on desktop
              if (window.innerWidth >= 640 && option.cols > 5) return null;
              const Icon = option.icon;
              return (
                <button
                  key={option.cols}
                  onClick={() => setGridColumns(option.cols)}
                  className={`p-2 rounded-md border transition-colors duration-200 ${
                    gridColumns === option.cols
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-500 border-gray-200 hover:bg-gray-100"
                  }`}
                  title={`Show ${option.cols} column${
                    option.cols > 1 ? "s" : ""
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Wishlist Content */}
        {wishlist.length === 0 ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Your wishlist is empty
            </h2>
            <p className="text-gray-600 mb-6">
              Start adding products you love to your wishlist.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div
            className={`grid gap-6 grid-cols-1 sm:grid-cols-2 ${
              gridColumns >= 3 ? `md:grid-cols-${gridColumns}` : ""
            } ${gridColumns >= 4 ? `lg:grid-cols-${gridColumns}` : ""} ${
              gridColumns === 5 ? "xl:grid-cols-5" : ""
            }`}
          >
            {wishlist.map((item) => {
              const product = item.product;
              console.log("Processing wishlist item:", {
                productId: product.id,
                productName: product.name,
                primaryImage: product.primary_image,
                variants: product.product_variants?.length || 0,
              });

              const defaultVariant =
                product.product_variants?.find((v) => v.is_default) ||
                product.product_variants?.[0];

              // Use the correct image field from the backend response
              const primaryImage =
                product.primary_image ||
                defaultVariant?.images?.find((img) => img.is_primary) ||
                defaultVariant?.images?.[0];

              console.log("Selected image:", primaryImage);

              return (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  {/* Product Image */}
                  <div className="relative aspect-square overflow-hidden bg-gray-100">
                    <Link to={`/products/${product.slug}`}>
                      <img
                        src={getImageUrl(primaryImage?.url)}
                        alt={primaryImage?.alt_text || product.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                        }}
                        loading="lazy"
                      />
                    </Link>

                    {/* Wishlist Remove Button */}
                    <button
                      onClick={() => handleRemoveFromWishlist(product.id)}
                      className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors duration-200"
                      title="Remove from wishlist"
                    >
                      <Heart className="w-5 h-5 text-red-500 fill-current" />
                    </button>

                    {/* Discount Badge */}
                    {product.has_discount && (
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-medium">
                        Sale
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-4">
                    {/* Brand */}
                    {product.brand && (
                      <p className="text-sm text-gray-500 mb-1">
                        {product.brand}
                      </p>
                    )}

                    {/* Product Name */}
                    <Link to={`/products/${product.slug}`}>
                      <h3 className="font-semibold text-gray-900 mb-2 hover:text-primary-600 transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                    </Link>

                    {/* Price with MRP */}
                    <div className="flex flex-col mb-3">
                      {product.has_discount && product.original_price ? (
                        <>
                          <span className="text-sm text-gray-500 line-through">
                            MRP ₹{product.original_price}
                          </span>
                          <span className="text-lg font-bold text-primary-600">
                            ₹{product.current_price || product.min_price}
                          </span>
                        </>
                      ) : (
                        <span className="text-lg font-bold text-primary-600">
                          MRP ₹{product.current_price || product.min_price}
                        </span>
                      )}
                    </div>

                    {/* Stock Status */}
                    <div className="mb-3">
                      {product.in_stock ? (
                        <span className="text-sm text-green-600 font-medium">
                          In Stock
                        </span>
                      ) : (
                        <span className="text-sm text-red-600 font-medium">
                          Out of Stock
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleAddToCart(product)}
                        disabled={!product.in_stock}
                        className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span>Add to Cart</span>
                      </button>

                      <Link
                        to={`/products/${product.slug}`}
                        className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Continue Shopping */}
        {wishlist.length > 0 && (
          <div className="mt-12 text-center">
            <Link
              to="/shop"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
            >
              Continue Shopping
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
